import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { throwApiError } from "@src/packages/api/api-error";
import { useApiClient } from "@src/packages/api/louise-api";
import { RequestBody } from "@src/packages/openapi/helpers";
import type { paths } from "@src/packages/openapi/schema";
export { createCycleMonitoringRequestId } from "@src/packages/care/monitoring/requestId";

type MonitoringEncountersRoute =
  paths["/{tenantId}/cycles/{cycleId}/monitoring-encounters"];
type MonitoringDecisionsRoute =
  paths["/{tenantId}/cycles/{cycleId}/monitoring-decisions"];

export type CycleMonitoringEncounter = NonNullable<
  MonitoringEncountersRoute["get"]["responses"]["200"]["content"]["application/json"]["data"]
>[number];

export type CycleMonitoringMeasurement =
  CycleMonitoringEncounter["measurements"][number];

export type CreateCycleMonitoringEncounterPayload = RequestBody<
  MonitoringEncountersRoute["post"]
>;

export type UpdateCycleMonitoringEncounterPayload = RequestBody<
  paths["/{tenantId}/cycles/{cycleId}/monitoring-encounters/{encounterId}"]["patch"]
>;

export type CycleMonitoringDecision = NonNullable<
  MonitoringDecisionsRoute["get"]["responses"]["200"]["content"]["application/json"]["data"]
>[number];

export type CreateCycleMonitoringDecisionPayload = RequestBody<
  MonitoringDecisionsRoute["post"]
>;

export function cycleMonitoringEncountersQueryKey(
  tenantId: string | undefined,
  cycleId: string | null | undefined,
) {
  return ["cycle-monitoring-encounters", tenantId, cycleId] as const;
}

export function cycleMonitoringDecisionsQueryKey(
  tenantId: string | undefined,
  cycleId: string | null | undefined,
) {
  return ["cycle-monitoring-decisions", tenantId, cycleId] as const;
}

export function useCycleMonitoringEncounters(
  cycleId: string | null | undefined,
) {
  const { client, tenantId } = useApiClient();
  const queryKey = cycleMonitoringEncountersQueryKey(tenantId, cycleId);

  return useQuery<CycleMonitoringEncounter[]>({
    queryKey,
    queryFn: async () => {
      if (!tenantId || !cycleId) return [];

      const { data, error } = await client.GET(
        "/{tenantId}/cycles/{cycleId}/monitoring-encounters",
        {
          params: {
            path: { tenantId, cycleId },
          },
        },
      );
      if (data) return data.data;
      throwApiError(
        error,
        "Failed to load monitoring encounters",
        "monitoring",
      );
    },
    enabled: Boolean(tenantId && cycleId),
  });
}

export function useCreateCycleMonitoringEncounter() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      payload,
    }: {
      cycleId: string;
      payload: CreateCycleMonitoringEncounterPayload;
    }) => {
      if (!tenantId) throw new Error("Cannot save monitoring without a tenant");

      const { data, error } = await client.POST(
        "/{tenantId}/cycles/{cycleId}/monitoring-encounters",
        {
          params: { path: { tenantId, cycleId } },
          body: payload,
        },
      );
      if (data) return data.data;
      throwApiError(error, "Failed to save monitoring", "monitoring");
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cycleMonitoringEncountersQueryKey(
          tenantId,
          variables.cycleId,
        ),
      });
    },
  });
}

export function useUpdateCycleMonitoringEncounter() {
  const { client, tenantId } = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      cycleId,
      encounterId,
      payload,
    }: {
      cycleId: string;
      encounterId: string;
      payload: UpdateCycleMonitoringEncounterPayload;
    }) => {
      if (!tenantId) throw new Error("Cannot save monitoring without a tenant");

      const { data, error } = await client.PATCH(
        "/{tenantId}/cycles/{cycleId}/monitoring-encounters/{encounterId}",
        {
          params: { path: { tenantId, cycleId, encounterId } },
          body: payload,
        },
      );
      if (data) return data.data;
      throwApiError(error, "Failed to update monitoring", "monitoring");
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: cycleMonitoringEncountersQueryKey(
          tenantId,
          variables.cycleId,
        ),
      });
    },
  });
}

export function useCycleMonitoringDecisions(
  cycleId: string | null | undefined,
) {
  const { client, tenantId } = useApiClient();

  return useQuery<CycleMonitoringDecision[]>({
    queryKey: cycleMonitoringDecisionsQueryKey(tenantId, cycleId),
    queryFn: async () => {
      if (!tenantId || !cycleId) return [];

      const { data, error } = await client.GET(
        "/{tenantId}/cycles/{cycleId}/monitoring-decisions",
        {
          params: { path: { tenantId, cycleId } },
        },
      );
      if (data) return data.data;
      throwApiError(error, "Failed to load monitoring decisions", "monitoring");
    },
    enabled: Boolean(tenantId && cycleId),
  });
}
